import { BasicResponse } from "../../../responses/basicResponses";
import { PanelTypeGenerationStatus } from "../../../responses/panelGeneratedModelDto";
import { PanelDockingPanel } from "../docking-panels/panelDockingPanel";
import { CreatePanelTypeOperationResult, PanelsModelEditor } from "../editors/panelsModelEditor";
import { IViewerAggregateSelectionChangedEventPayload } from "../viewer-utils/viewerEventPayloads";
import { ElementParameterChangedEventPayload } from "../eventBus/elementParameterChangedEventPayload";
import {
    CreateNewPanelTypeEventPayload,
    PanelTypeSwitchedEventPayload,
    SwitchPanelTypeEventPayload,
    UpdateLouvreCutoutsEventPayload,
    UpdatePanelTypeEventPayload,
    UpdateWindowDesignParametersEventPayload,
} from "../eventBus/typesManagerEventPayloads";
import eventBus, { IApplicationEvent } from "../eventBus/eventDispatcher";

export type ForgePanelsEditorLoadOptions = {
    panelsEditor: PanelsModelEditor;
};

export class ForgePanelsEditor extends Autodesk.Viewing.Extension {
    private readonly panelsEditor: PanelsModelEditor;
    private editorPanel: PanelDockingPanel | null = null;

    constructor(viewer: Autodesk.Viewing.GuiViewer3D, options: ForgePanelsEditorLoadOptions) {
        super(viewer, options);

        this.panelsEditor = options.panelsEditor;
        this.onSelectionChanged = this.onSelectionChanged.bind(this);
        this.onPanelParametersChanged = this.onPanelParametersChanged.bind(this);
        this.onUpdatePanelType = this.onUpdatePanelType.bind(this);
        this.onCreatePanelType = this.onCreatePanelType.bind(this);
        this.onSwitchPanelType = this.onSwitchPanelType.bind(this);
        this.onPanelTypeSwitched = this.onPanelTypeSwitched.bind(this);
        this.onSwitchPanelCustomPanelType = this.onSwitchPanelCustomPanelType.bind(this);
        this.onRestoreRegularPanelType = this.onRestoreRegularPanelType.bind(this);
        this.onUpdateWindowDesignParameters = this.onUpdateWindowDesignParameters.bind(this);
        this.onUpdateLouvreCutouts = this.onUpdateLouvreCutouts.bind(this);
    }

    load() {
        this.panelsEditor.createPanels();
        this.viewer.addEventListener(Autodesk.Viewing.AGGREGATE_SELECTION_CHANGED_EVENT, this.onSelectionChanged);

        eventBus.addEventListener("Dextall.Panels.UI.ParametersChanged", this.onPanelParametersChanged);
        eventBus.addEventListener("Dextall.PanelTypes.Update", this.onUpdatePanelType);
        eventBus.addEventListener("Dextall.PanelTypes.NewPanelType", this.onCreatePanelType);
        eventBus.addEventListener("Dextall.PanelTypes.SwitchPanelTypeRequested", this.onSwitchPanelType);
        eventBus.addEventListener("Dextall.PanelTypes.PanelTypeSwitched", this.onPanelTypeSwitched);
        eventBus.addEventListener("Dextall.PanelTypes.UpdateWindowDesignParameters", this.onUpdateWindowDesignParameters);
        eventBus.addEventListener("Dextall.PanelTypes.UpdateLouvreCutouts", this.onUpdateLouvreCutouts);
        eventBus.addEventListener("Dextall.Panels.UI.SwitchToCustomPanelType", this.onSwitchPanelCustomPanelType);
        eventBus.addEventListener("Dextall.Panels.UI.RestoreRegularPanelType", this.onRestoreRegularPanelType);

        return true;
    }

    unload() {
        this.viewer.removeEventListener(Autodesk.Viewing.AGGREGATE_SELECTION_CHANGED_EVENT, this.onSelectionChanged);

        eventBus.removeEventListener("Dextall.Panels.UI.ParametersChanged", this.onPanelParametersChanged);
        eventBus.removeEventListener("Dextall.PanelTypes.Update", this.onUpdatePanelType);
        eventBus.removeEventListener("Dextall.PanelTypes.NewPanelType", this.onCreatePanelType);
        eventBus.removeEventListener("Dextall.PanelTypes.SwitchPanelTypeRequested", this.onSwitchPanelType);
        eventBus.removeEventListener("Dextall.PanelTypes.PanelTypeSwitched", this.onPanelTypeSwitched);
        eventBus.removeEventListener("Dextall.PanelTypes.UpdateWindowDesignParameters", this.onUpdateWindowDesignParameters);
        eventBus.removeEventListener("Dextall.PanelTypes.UpdateLouvreCutouts", this.onUpdateLouvreCutouts);
        eventBus.removeEventListener("Dextall.Panels.UI.SwitchToCustomPanelType", this.onSwitchPanelCustomPanelType);
        eventBus.removeEventListener("Dextall.Panels.UI.RestoreRegularPanelType", this.onRestoreRegularPanelType);

        this.editorPanel?.shutdown();

        return true;
    }

    onToolbarCreated() {
        this.editorPanel = new PanelDockingPanel(this.viewer.container);
    }

    private onSelectionChanged(event: IViewerAggregateSelectionChangedEventPayload) {
        const selection = event
            .selections
            .find((x: { model: { id: number; }; }) => x.model.id === this.panelsEditor.modelId);

        const elementDbId = selection?.dbIdArray[0];

        this.raiseSelectionChanged(elementDbId);
    }

    private async onPanelParametersChanged(event: IApplicationEvent<ElementParameterChangedEventPayload>) {
        const { id, elementName, installationSequence } = event.payload;
        let savedParameters = {
            elementName: this.panelsEditor.findPanelById(id)?.elementName ?? "",
            installationSequence: this.panelsEditor.findPanelById(id)?.installationSequence ?? null,
        };

        const undoRedo = async () => {
            const temp = {
                elementName: this.panelsEditor.findPanelById(id)?.elementName ?? "",
                installationSequence: this.panelsEditor.findPanelById(id)?.installationSequence ?? null,
            };
            const result = await this.panelsEditor.update({
                panelId: id,
                ...savedParameters,
            });
            this.refreshSelection(result);
            savedParameters = temp;
        };

        eventBus.dispatchEvent({
            type: "Dextall.UndoRedo.PushAction",
            payload: {
                action: { name: "Update panel", undo: undoRedo, redo: undoRedo },
            },
        });

        const result = await this.panelsEditor.update({
            panelId: id,
            elementName,
            installationSequence,
        });

        this.refreshSelection(result);
    }

    private async onUpdatePanelType(event: IApplicationEvent<UpdatePanelTypeEventPayload>) {
        const panelType = this.panelsEditor.findPanelType(event.payload.id);
        if (!panelType) {
            throw new Error("Invalid state!");
        }

        let savedPanelProperties: UpdatePanelTypeEventPayload = {
            id: panelType.id,
            name: panelType.name,
            withoutCladdings: panelType.withoutCladdings,
        };

        const undoRedo = async () => {
            const panelType = this.panelsEditor.findPanelType(savedPanelProperties.id);
            if (!panelType) {
                throw new Error("Invalid state!");
            }

            const temp: UpdatePanelTypeEventPayload = {
                id: panelType.id,
                name: panelType.name,
                withoutCladdings: panelType.withoutCladdings,
            };

            const result = await this.panelsEditor.updatePanelType(savedPanelProperties);
            this.refreshSelection(result);

            savedPanelProperties = temp;
        };

        const result = await this.panelsEditor.updatePanelType(event.payload);

        this.refreshSelection(result);

        eventBus.dispatchEvent({
            type: "Dextall.UndoRedo.PushAction",
            payload: {
                action: { name: "Update panel type", undo: undoRedo, redo: undoRedo },
            },
        });

        eventBus.dispatchEvent({
            type: "Dextall.TypesManager.UI.PanelType.SetGeneratedModel",
            payload: {
                model: { id: "", bubble: null, status: PanelTypeGenerationStatus.None },
                panelTypeId: event.payload.id,
                drawings: [],
            },
        });
    }

    private async onUpdateWindowDesignParameters(event: IApplicationEvent<UpdateWindowDesignParametersEventPayload>) {
        const { panelTypeId, windowIndex, area, weight } = event.payload;

        const panelType = this.panelsEditor.findPanelType(panelTypeId);
        if (!panelType) {
            throw new Error(`Invalid state! Panel type with id ${panelTypeId} not found.`);
        }

        let savedParameters = structuredClone(panelType.windowsDesignParameters[windowIndex]);

        const undoRedo = async () => {
            const panelType = this.panelsEditor.findPanelType(panelTypeId);
            if (!panelType) {
                throw new Error(`Invalid state! Panel type with id ${panelTypeId} not found.`);
            }

            const temp = structuredClone(panelType.windowsDesignParameters[windowIndex]);
            const result = await this.panelsEditor.setPanelTypeWindowDesignParameters(
                panelTypeId,
                windowIndex,
                savedParameters,
            );
            this.refreshSelection(result);
            eventBus.dispatchEvent({
                type: "Dextall.Hooks.Designer.Panel.ComponentSelectionChanged",
                payload: null,
            });

            savedParameters = temp;
        };

        const operationResult = await this.panelsEditor.setPanelTypeWindowDesignParameters(panelTypeId, windowIndex, {
            area,
            weight,
        });
        if (operationResult.isSuccess) {
            eventBus.dispatchEvent({
                type: "Dextall.UndoRedo.PushAction",
                payload: {
                    action: { name: "Update window design parameters", undo: undoRedo, redo: undoRedo },
                },
            });

            eventBus.dispatchEvent({
                type: "Dextall.TypesManager.UI.PanelType.SetGeneratedModel",
                payload: {
                    model: { id: "", bubble: null, status: PanelTypeGenerationStatus.None },
                    panelTypeId,
                    drawings: [],
                },
            });
        } else {
            this.notifyError(operationResult.message);
        }

        eventBus.dispatchEvent({
            type: "Dextall.Hooks.Designer.Panel.ComponentSelectionChanged",
            payload: null,
        });
    }

    private async onUpdateLouvreCutouts(event: IApplicationEvent<UpdateLouvreCutoutsEventPayload>) {
        const { panelTypeId, louvreIndex, rectangularCutouts, circularCutouts } = event.payload;

        const panelType = this.panelsEditor.findPanelType(panelTypeId);
        if (!panelType) {
            throw new Error(`Invalid state! Panel type with id ${panelTypeId} not found.`);
        }

        let savedCutouts = structuredClone(panelType.louvreCutouts[louvreIndex]);

        const undoRedo = async () => {
            const panelType = this.panelsEditor.findPanelType(panelTypeId);
            if (!panelType) {
                throw new Error(`Invalid state! Panel type with id ${panelTypeId} not found.`);
            }

            const temp = structuredClone(panelType.louvreCutouts[louvreIndex]);
            const result = await this.panelsEditor.setPanelTypeLouvreCutouts(
                panelTypeId,
                louvreIndex,
                savedCutouts,
            );
            this.refreshSelection(result);
            eventBus.dispatchEvent({
                type: "Dextall.Hooks.Designer.Panel.ComponentSelectionChanged",
                payload: null,
            });

            savedCutouts = temp;
        };

        const operationResult = await this.panelsEditor.setPanelTypeLouvreCutouts(panelTypeId, louvreIndex, {
            rectangularCutouts,
            circularCutouts,
        });
        if (operationResult.isSuccess) {
            eventBus.dispatchEvent({
                type: "Dextall.UndoRedo.PushAction",
                payload: {
                    action: { name: "Update louvre cutouts", undo: undoRedo, redo: undoRedo },
                },
            });

            eventBus.dispatchEvent({
                type: "Dextall.TypesManager.UI.PanelType.SetGeneratedModel",
                payload: {
                    model: { id: "", bubble: null, status: PanelTypeGenerationStatus.None },
                    panelTypeId,
                    drawings: [],
                },
            });
            eventBus.dispatchEvent({
                type: "Dextall.Hooks.Designer.Panel.ComponentSelectionChanged",
                payload: null,
            });
        } else {
            this.notifyError(operationResult.message);
        }
        eventBus.dispatchEvent({
            type: "Dextall.PanelTypes.UI.LouvreCutoutsSaved",
            payload: operationResult.isSuccess,
        });
    }

    private async onSwitchPanelType(event: IApplicationEvent<SwitchPanelTypeEventPayload>) {
        const { panelId, targetTypeId } = event.payload;
        let savedPanelTypeId = this.panelsEditor.findPanelById(panelId)?.panelTypeId ?? "";

        const undoRedo = async () => {
            const temp = this.panelsEditor.findPanelById(panelId)?.panelTypeId ?? "";
            const result = await this.panelsEditor.switchPanelType(panelId, savedPanelTypeId);
            this.refreshPanelType(result, panelId, savedPanelTypeId);
            savedPanelTypeId = temp;
        };

        eventBus.dispatchEvent({
            type: "Dextall.UndoRedo.PushAction",
            payload: {
                action: { name: "Switch panel type", undo: undoRedo, redo: undoRedo },
            },
        });

        const result = await this.panelsEditor.switchPanelType(panelId, targetTypeId);
        this.refreshPanelType(result, panelId, targetTypeId);
    }

    private async onSwitchPanelCustomPanelType(event: IApplicationEvent<SwitchPanelTypeEventPayload>) {
        const { panelId, targetTypeId } = event.payload;

        const undo = async () => {
            const result = await this.panelsEditor.switchPanelCustomType(panelId, null);
            this.refreshPanelType(result, panelId);

            eventBus.dispatchEvent({
                type: "Dextall.Panels.UI.RegularPanelTypeRestored",
                payload: panelId,
            });
        };

        const redo = async () => {
            const result = await this.panelsEditor.switchPanelCustomType(panelId, targetTypeId);
            this.refreshPanelType(result, panelId);

            eventBus.dispatchEvent({
                type: "Dextall.Panels.UI.SwitchedToCustomPanelType",
                payload: { panelId, targetTypeId },
            });
        };

        eventBus.dispatchEvent({
            type: "Dextall.UndoRedo.PushAction",
            payload: {
                action: { name: "Assign custom type to panel", undo, redo },
            },
        });

        const result = await this.panelsEditor.switchPanelCustomType(panelId, targetTypeId);
        this.refreshPanelType(result, panelId);

        eventBus.dispatchEvent({
            type: "Dextall.Panels.UI.SwitchedToCustomPanelType",
            payload: { panelId, targetTypeId },
        });
    }

    private async onRestoreRegularPanelType(event: IApplicationEvent<string>) {
        const panelId = event.payload;
        const customPanelTypeId = this.panelsEditor.findPanelById(panelId)?.customPanelTypeId;

        const undo = async () => {
            if (customPanelTypeId) {
                const result = await this.panelsEditor.switchPanelCustomType(panelId, customPanelTypeId);
                this.refreshPanelType(result, panelId);

                eventBus.dispatchEvent({
                    type: "Dextall.Panels.UI.SwitchedToCustomPanelType",
                    payload: { panelId, targetTypeId: customPanelTypeId },
                });
            }
        };

        const redo = async () => {
            const result = await this.panelsEditor.switchPanelCustomType(panelId, null);
            this.refreshPanelType(result, panelId);

            eventBus.dispatchEvent({
                type: "Dextall.Panels.UI.RegularPanelTypeRestored",
                payload: panelId,
            });
        };

        eventBus.dispatchEvent({
            type: "Dextall.UndoRedo.PushAction",
            payload: {
                action: { name: "Reset to standard type", undo, redo },
            },
        });

        const result = await this.panelsEditor.switchPanelCustomType(panelId, null);

        this.refreshPanelType(result, panelId);

        eventBus.dispatchEvent({
            type: "Dextall.Panels.UI.RegularPanelTypeRestored",
            payload: panelId,
        });
    }

    private async onCreatePanelType(event: IApplicationEvent<CreateNewPanelTypeEventPayload>) {
        const { panelId } = event.payload;
        let savedPanelTypeId = this.panelsEditor.findPanelById(panelId)?.panelTypeId ?? "";

        const undoRedo = async () => {
            const temp = this.panelsEditor.findPanelById(panelId)?.panelTypeId ?? "";
            const result = await this.panelsEditor.switchPanelType(panelId, savedPanelTypeId);
            this.refreshPanelType(result, panelId, savedPanelTypeId);
            savedPanelTypeId = temp;
        };

        eventBus.dispatchEvent({
            type: "Dextall.UndoRedo.PushAction",
            payload: {
                action: { name: "Switch panel type to new type", undo: undoRedo, redo: undoRedo },
            },
        });

        const result = await this.panelsEditor.createPanelType(event.payload, panelId);

        this.refreshPanelType(result, panelId);
    }

    private onPanelTypeSwitched(event: IApplicationEvent<PanelTypeSwitchedEventPayload>) {
        this.panelsEditor.updatePanelTypeTexts(event.payload.panelId);
    }

    private refreshPanelType(result: CreatePanelTypeOperationResult, panelId: string, targetPanelTypeId?: string) {
        if (result.isSuccess) {
            eventBus.dispatchEvent({
                type: "Dextall.PanelTypes.PanelTypeSwitched",
                payload: { panelId, targetPanelTypeId, removedHooksDbIds: result.removedHooksDbIds },
            });
        }

        this.refreshSelection(result);
    }

    private refreshSelection(operationResult: BasicResponse) {
        if (!operationResult.isSuccess) {
            this.notifyError(operationResult.message);
        }

        const selection = this.viewer.getAggregateSelection().find(x => x.model.id === this.panelsEditor.modelId);

        this.raiseSelectionChanged(selection?.selection[0]);
    }

    private raiseSelectionChanged(elementDbId: number | undefined) {
        const panel = elementDbId !== undefined ? this.panelsEditor.findPanel(elementDbId) : undefined;

        const family = panel && this.panelsEditor.findPanelFamily(panel.panelTypeId);

        eventBus.dispatchEvent({
            type: "Dextall.Panels.SelectionChanged",
            payload: (panel && family) ? {
                panel,
                family,
                customPanelTypes: this.panelsEditor.findValidCustomPanelTypesForPanel(panel.id),
            } : null,
        });

        this.editorPanel?.setVisible(panel !== undefined);
    }

    private notifyError(message: string) {
        eventBus.dispatchEvent({
            type: "Dextall.Common.Notify.Error",
            payload: message,
        });
    }
}

export const forgePanelsEditorName = "Dextall.ForgePanelsEditor" as const;

Autodesk.Viewing.theExtensionManager.registerExtension(forgePanelsEditorName, ForgePanelsEditor);
